import React, { useState, useEffect, useCallback } from 'react'
import {
  Box,
  Paper,
  Typography,
  IconButton,
  Chip,
  Tooltip,
  Alert,
  CircularProgress,
  TextField,
  MenuItem,
  Collapse,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
} from '@mui/material'
import RefreshIcon from '@mui/icons-material/Refresh'
import AssignmentIcon from '@mui/icons-material/Assignment'
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp'
import { crqDeploymentApi } from '../utils/api'

const CRQ_STATUSES = [
  { value: '', label: 'All Statuses' },
  { value: 'PENDING_CREATION', label: 'Pending Creation' },
  { value: 'CREATED', label: 'Created' },
  { value: 'PENDING_APPROVAL', label: 'Pending Approval' },
  { value: 'APPROVED', label: 'Approved' },
  { value: 'SCHEDULED', label: 'Scheduled' },
  { value: 'IN_PROGRESS', label: 'In Progress' },
  { value: 'COMPLETED', label: 'Completed' },
  { value: 'FAILED', label: 'Failed' },
  { value: 'CANCELLED', label: 'Cancelled' },
  { value: 'ELAPSED', label: 'Elapsed' },
]

const ENVIRONMENTS = [
  { value: '', label: 'All Environments' },
  { value: 'dev', label: 'DEV' },
  { value: 'qa', label: 'QA' },
  { value: 'uat', label: 'UAT' },
  { value: 'prd', label: 'PRD' },
]

const formatStatus = (status) => {
  const statusMap = {
    PENDING_CREATION: 'Pending Creation',
    CREATED: 'Created',
    PENDING_APPROVAL: 'Pending Approval',
    APPROVED: 'Approved',
    SCHEDULED: 'Scheduled',
    IN_PROGRESS: 'In Progress',
    COMPLETED: 'Completed',
    FAILED: 'Failed',
    CANCELLED: 'Cancelled',
    ELAPSED: 'Elapsed',
  }
  return statusMap[status] || status
}

const getStatusColor = (status) => {
  switch (status) {
    case 'COMPLETED':
      return 'success'
    case 'IN_PROGRESS':
    case 'APPROVED':
    case 'SCHEDULED':
      return 'info'
    case 'PENDING_CREATION':
    case 'PENDING_APPROVAL':
    case 'CREATED':
      return 'warning'
    case 'FAILED':
    case 'CANCELLED':
    case 'ELAPSED':
      return 'error'
    default:
      return 'default'
  }
}

const getRenewalStatusColor = (status) => {
  switch (status) {
    case 'RENEWED':
    case 'PUSHED':
      return 'success'
    case 'RENEWING':
      return 'info'
    case 'PENDING':
      return 'warning'
    case 'FAILED':
      return 'error'
    case 'SKIPPED':
      return 'default'
    default:
      return 'default'
  }
}

const formatDate = (dateStr) => {
  if (!dateStr) return '—'
  return new Date(dateStr).toLocaleString()
}

const formatDateShort = (dateStr) => {
  if (!dateStr) return '—'
  return new Date(dateStr).toLocaleDateString()
}

const CRQs = () => {
  const [crqDeployments, setCrqDeployments] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [rowCount, setRowCount] = useState(0)
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 25,
  })
  const [expandedRowIds, setExpandedRowIds] = useState(new Set())

  // Filters
  const [statusFilter, setStatusFilter] = useState('')
  const [environmentFilter, setEnvironmentFilter] = useState('')

  const fetchCRQs = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const params = {
        page: paginationModel.page + 1, // Backend is 1-indexed
        page_size: paginationModel.pageSize,
      }
      if (statusFilter) params.status = statusFilter
      if (environmentFilter) params.environment = environmentFilter

      const response = await crqDeploymentApi.getAll(params)
      const { items, total } = response.data

      const mapped = items.map((crq) => ({
        id: crq.crq_id,
        crqNumber: crq.crq_number || '—',
        crqType: crq.crq_type || 'RENEWAL',
        productCode: crq.product_code,
        productName: crq.product_name || crq.product_code,
        environment: crq.environment,
        status: crq.status,
        scheduledStart: crq.scheduled_start_date,
        scheduledEnd: crq.scheduled_end_date,
        windowStart: crq.window_start,
        windowEnd: crq.window_end,

        crqCreatedAt: crq.crq_created_at,
        renewalStartedAt: crq.renewal_started_at,
        renewalCompletedAt: crq.renewal_completed_at,
        reminderCount: crq.reminder_count || 0,
        errorMessage: crq.error_message,
        createdAt: crq.created_at,
        updatedAt: crq.updated_at,
        certificates: crq.certificates || [],
        certCount: (crq.certificates || []).length,
      }))

      setCrqDeployments(mapped)
      setRowCount(total)
    } catch (err) {
      console.error('Error fetching CRQ deployments:', err)
      setError(err.response?.data?.detail || 'Failed to load CRQ deployments. Please try again.')
    } finally {
      setLoading(false)
    }
  }, [paginationModel, statusFilter, environmentFilter])

  useEffect(() => {
    fetchCRQs()
  }, [fetchCRQs])

  const toggleRow = (id) => {
    setExpandedRowIds((prev) => {
      const next = new Set(prev)
      if (next.has(id)) {
        next.delete(id)
      } else {
        next.add(id)
      }
      return next
    })
  }

  // Custom detail panel rendered inline below each row
  const DetailPanel = ({ row }) => {
    const certs = row.certificates || []
    if (certs.length === 0) {
      return (
        <Box sx={{ p: 2, pl: 8 }}>
          <Typography variant="body2" color="text.secondary">
            No certificates linked to this CRQ.
          </Typography>
        </Box>
      )
    }

    return (
      <Box sx={{ p: 2, pl: 8, pr: 4, bgcolor: 'grey.50' }}>
        <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
          Certificates ({certs.length})
        </Typography>
        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: 'grey.100' }}>
                <TableCell sx={{ fontWeight: 'bold' }}>Common Name</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Venafi Cert ID (DN)</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Cert GUID</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Renewal Status</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Venafi Expiration</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Renewed At</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Pushed At</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Error</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {certs.map((cert) => (
                <TableRow key={cert.id} hover>
                  <TableCell>{cert.common_name || '—'}</TableCell>
                  <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.75rem', wordBreak: 'break-all', maxWidth: 320 }}>
                    <Tooltip title={cert.venafi_cert_id || '—'}>
                      <span>{cert.venafi_cert_id || '—'}</span>
                    </Tooltip>
                  </TableCell>
                  <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>
                    {cert.cert_id}
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={cert.renewal_status}
                      size="small"
                      color={getRenewalStatusColor(cert.renewal_status)}
                    />
                  </TableCell>
                  <TableCell>{formatDateShort(cert.venafi_expiration_date)}</TableCell>
                  <TableCell>{formatDate(cert.renewed_at)}</TableCell>
                  <TableCell>{formatDate(cert.pushed_at)}</TableCell>
                  <TableCell>
                    {cert.error_message ? (
                      <Tooltip title={cert.error_message}>
                        <Chip label="Error" size="small" color="error" variant="outlined" />
                      </Tooltip>
                    ) : (
                      '—'
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        {row.errorMessage && (
          <Alert severity="error" sx={{ mt: 1 }}>
            CRQ Error: {row.errorMessage}
          </Alert>
        )}
      </Box>
    )
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <AssignmentIcon sx={{ fontSize: 40, color: 'primary.main' }} />
          <Typography variant="h4" sx={{ fontWeight: 600, color: 'primary.main' }}>
            CRQ Deployments
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Tooltip title="Refresh">
            <IconButton onClick={fetchCRQs} color="primary">
              <RefreshIcon />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {/* Filters */}
      <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
        <TextField
          select
          label="Status"
          value={statusFilter}
          onChange={(e) => {
            setStatusFilter(e.target.value)
            setPaginationModel((prev) => ({ ...prev, page: 0 }))
          }}
          size="small"
          sx={{ minWidth: 180 }}
        >
          {CRQ_STATUSES.map((opt) => (
            <MenuItem key={opt.value} value={opt.value}>
              {opt.label}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          select
          label="Environment"
          value={environmentFilter}
          onChange={(e) => {
            setEnvironmentFilter(e.target.value)
            setPaginationModel((prev) => ({ ...prev, page: 0 }))
          }}
          size="small"
          sx={{ minWidth: 160 }}
        >
          {ENVIRONMENTS.map((opt) => (
            <MenuItem key={opt.value} value={opt.value}>
              {opt.label}
            </MenuItem>
          ))}
        </TextField>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Paper elevation={2} sx={{ p: 3, borderRadius: 2 }}>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: 'grey.100' }}>
                <TableCell sx={{ width: 50 }} />
                <TableCell sx={{ fontWeight: 'bold' }}>CRQ Number</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Type</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Product</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Environment</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Status</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }} align="center">Certs</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Scheduled Start</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Scheduled End</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Expiry Window Start</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Expiry Window End</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }} align="center">Reminders</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Created</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={13} align="center" sx={{ py: 4 }}>
                    <CircularProgress />
                  </TableCell>
                </TableRow>
              ) : crqDeployments.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={13} align="center" sx={{ py: 4 }}>
                    <Typography variant="body2" color="text.secondary">No CRQ deployments found.</Typography>
                  </TableCell>
                </TableRow>
              ) : (
                crqDeployments.map((row) => (
                  <React.Fragment key={row.id}>
                    <TableRow
                      hover
                      onClick={() => toggleRow(row.id)}
                      sx={{ cursor: 'pointer', '& > *': { borderBottom: expandedRowIds.has(row.id) ? 'none' : undefined } }}
                    >
                      <TableCell>
                        <IconButton
                          size="small"
                          onClick={(e) => { e.stopPropagation(); toggleRow(row.id) }}
                          aria-label={expandedRowIds.has(row.id) ? 'Collapse certificates' : 'Expand certificates'}
                        >
                          {expandedRowIds.has(row.id) ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                        </IconButton>
                      </TableCell>
                      <TableCell>{row.crqNumber}</TableCell>
                      <TableCell>
                        <Chip
                          label={row.crqType === 'LAYER7' ? 'Layer7' : 'Renewal'}
                          size="small"
                          color={row.crqType === 'LAYER7' ? 'secondary' : 'primary'}
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell>{row.productName}</TableCell>
                      <TableCell>
                        <Chip
                          label={row.environment?.toUpperCase()}
                          size="small"
                          color={row.environment === 'prd' ? 'error' : 'default'}
                        />
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={formatStatus(row.status)}
                          size="small"
                          color={getStatusColor(row.status)}
                        />
                      </TableCell>
                      <TableCell align="center">
                        <Chip label={row.certCount} size="small" variant="outlined" />
                      </TableCell>
                      <TableCell>{formatDate(row.scheduledStart)}</TableCell>
                      <TableCell>{formatDate(row.scheduledEnd)}</TableCell>
                      <TableCell>{formatDateShort(row.windowStart)}</TableCell>
                      <TableCell>{formatDateShort(row.windowEnd)}</TableCell>
                      <TableCell align="center">{row.reminderCount}</TableCell>
                      <TableCell>{formatDate(row.createdAt)}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell colSpan={13} sx={{ py: 0, border: expandedRowIds.has(row.id) ? undefined : 'none' }}>
                        <Collapse in={expandedRowIds.has(row.id)} timeout="auto" unmountOnExit>
                          <DetailPanel row={row} />
                        </Collapse>
                      </TableCell>
                    </TableRow>
                  </React.Fragment>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={rowCount}
          page={paginationModel.page}
          onPageChange={(_, newPage) => setPaginationModel((prev) => ({ ...prev, page: newPage }))}
          rowsPerPage={paginationModel.pageSize}
          onRowsPerPageChange={(e) => setPaginationModel({ page: 0, pageSize: parseInt(e.target.value, 10) })}
          rowsPerPageOptions={[10, 25, 50, 100]}
        />
      </Paper>
    </Box>
  )
}

export default CRQs
